import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box,
  Grid,
  IconButton,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  QuestionMark as QuestionIcon,
  RemoveRedEye as EyeIcon,
} from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { StateReasonModal, type StateReasonModalType } from './components';
import { HeadCell, Row, RowCell } from '../../../components/table/types';
import { ViewDocumentModal } from '../../../components/ViewDocumentModal';

import {
  useLazyGetBankAccountsQuery,
  useUpdateBankAccountMutation,
  useUpdateBankAccountStateMutation,
} from '../../../api';
import { UTCToDateTime } from '../../../utils/dates';
import { BankAccountState } from '../../../enums';
import type { BankAccount } from '../../../types';
import { permissionsCheck } from '../../../utils/permission';
import { EditBankAccount } from './components/EditBankAccount.tsx';

export const BankAccounts: FC = () => {
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditBankAccounts = permissionsCheck(['WRITE_APPLICATIONS']);

  // api data
  const [getAccounts, { data: accounts, isLoading: isAccountsLoading }] =
    useLazyGetBankAccountsQuery();
  const [updateAccount, { isLoading: isAccountUpdateLoading }] = useUpdateBankAccountMutation();
  const [updateAccountState, { isLoading: isAccountUpdateStateLoading }] =
    useUpdateBankAccountStateMutation();

  // fetch accounts
  useEffect(() => {
    if (!profileUid) return;

    getAccounts({ profileUid });
  }, [profileUid]);

  // variables
  const isLoading = isAccountsLoading || isAccountUpdateLoading || isAccountUpdateStateLoading;

  // document variables
  const [selectedFile, setSelectedFile] = useState<{ url: string; fileType: string } | null>(null);
  const [openViewDocument, setOpenViewDocument] = useState(false);

  // edit fields logic
  const [editFields, setEditFields] = useState<Partial<BankAccount> | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // edit mode logic
  const [activeAccount, setActiveAccount] = useState<BankAccount | null>(null);
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (account: BankAccount) => {
    setEditFields(account);
    setActiveAccount(account);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveAccount(null);
    setEditMode(false);
  };

  // reason for rejected status logic
  const [reasonModalShown, setReasonModalShown] = useState(false);
  const [reasonModalType, setReasonModalType] = useState<StateReasonModalType>('show');
  const [shownStateReason, setShownStateReason] = useState('');
  const openReasonModal = (modalType: StateReasonModalType, state_reason?: string) => {
    setReasonModalShown(true);
    setReasonModalType(modalType);
    state_reason && setShownStateReason(state_reason);
  };
  const closeReasonModal = () => {
    setReasonModalShown(false);
    setShownStateReason('');
  };
  const cancelReasonModal = () => {
    if (activeAccount?.state && !activeAccount?.state_reason) {
      setEditFields({ ...editFields, state: activeAccount.state, state_reason: '' });
    } else if (!activeAccount?.state_reason) {
      setEditFields({ ...editFields, state_reason: '' });
    }

    setReasonModalShown(false);
  };

  // useEffect(() => {
  //   if (editFields?.state === BankAccountState.REJECTED && !activeAccount?.state_reason) {
  //     openReasonModal('edit');
  //   } else if (!activeAccount?.state_reason) {
  //     setEditFields({ ...editFields, state_reason: '' });
  //   }
  // }, [editFields?.state]);

  // edit rate markup logic
  const editBankAccount = async () => {
    if (!editFields || !profileUid) return;

    const argsForOther = {
      profileUid,
      accountId: editFields.id!,
      body: {
        bank_name: editFields.bank_name,
        account: editFields.account,
        swift: editFields.swift,
        reference: editFields.reference,
        beneficiary_name: editFields.beneficiary_name,
        bank_address: editFields.bank_address,
        type: editFields.type,
      },
    };
    const argsForState = {
      profileUid,
      accountId: editFields.id!,
      body: {
        state: editFields.state!,
        state_reason:
          editFields.state === BankAccountState.REJECTED ? editFields.state_reason : undefined,
      },
    };

    try {
      await updateAccount(argsForOther).unwrap();
      await updateAccountState(argsForState).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      offEditMode();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    } finally {
      await getAccounts({ profileUid }).unwrap();
    }
  };

  // get path for downloading
  const getPath = (path: string) => {
    if (path.startsWith('http') || path.startsWith('https')) {
      return path;
    }

    return `https://${path}`;
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('profile-bank-accounts.head-cells.created-at'),
    },
    {
      id: '9',
      label: t('profile-bank-accounts.head-cells.type'),
    },
    {
      id: '2',
      label: t('profile-bank-accounts.head-cells.account'),
    },
    {
      id: '3',
      label: t('profile-bank-accounts.head-cells.bank'),
    },
    {
      id: '4',
      label: t('profile-bank-accounts.head-cells.reference'),
    },
    {
      id: '5',
      label: t('profile-bank-accounts.head-cells.state'),
    },
    {
      id: '6',
      label: t('profile-bank-accounts.head-cells.verified-at'),
    },
    {
      id: '7',
      label: t('profile-bank-accounts.head-cells.archived-at'),
    },
    {
      id: '8',
      label: t('profile-bank-accounts.head-cells.actions'),
      hidden: !hasPermissionToEditBankAccounts,
    },
  ];

  const rows: Row[] | undefined = accounts?.accounts?.map((it) => {
    const {
      id,
      created_at,
      bank_name,
      swift,
      type,
      account,
      state,
      verified_at,
      archived_at,
      document_path,
      beneficiary_name,
      bank_address,
      reference,
      state_reason,
    } = it;

    const cells: RowCell[] = [
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'type',
        value: type,
        sx: { fontWeight: 'bold' },
      },
      {
        name: 'beneficiary_name',
        value: beneficiary_name,
        component: (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span>{beneficiary_name}</span>
            <span>{account}</span>
          </Box>
        ),
      },
      {
        name: 'bank_name',
        value: bank_name,
        component: (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <span>{bank_name}</span>
            <span>{swift}</span>
            <span>{bank_address}</span>
          </Box>
        ),
      },
      {
        name: 'reference',
        value: reference,
      },
      {
        name: 'state',
        value: state,
        component: (
          <Stack direction="row" spacing={2} alignItems="center">
            {state_reason && (
              <Tooltip title={t('profile-bank-accounts.state-reason-modal.tooltip')}>
                <IconButton
                  aria-label="reason"
                  color="info"
                  onClick={() => openReasonModal('show', state_reason)}
                  disabled={isLoading}
                >
                  <QuestionIcon />
                </IconButton>
              </Tooltip>
            )}

            <div>{t(`common.bank-account-state.${state}`)}</div>
          </Stack>
        ),
      },
      {
        name: 'verified_at',
        value: verified_at,
        formatting: (value: string | null) => {
          return value ? UTCToDateTime(value) : '–';
        },
      },
      {
        name: 'archived_at',
        value: archived_at,
        formatting: (value: string | null) => {
          return value ? UTCToDateTime(value) : '–';
        },
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditBankAccounts,
        component: (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={() => startEditMode(it)}
              disabled={Boolean(archived_at) || isLoading}
            >
              <EditIcon />
            </IconButton>

            {document_path && (
              <IconButton
                aria-label="open"
                color="info"
                onClick={() => {
                  const splitFileName = document_path?.split('.');
                  const fileType = splitFileName[splitFileName.length - 1];
                  setSelectedFile({
                    url: getPath(document_path),
                    fileType: fileType,
                  });
                  setOpenViewDocument(true);
                }}
                disabled={isLoading}
              >
                <EyeIcon />
              </IconButton>
            )}
          </Stack>
        ),
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <ViewDocumentModal
        onClose={() => setOpenViewDocument(false)}
        item={selectedFile}
        open={openViewDocument}
      />

      <EditBankAccount
        open={isEditMode}
        onSave={editBankAccount}
        onCancel={offEditMode}
        fields={editFields}
        fieldsHandler={handleEditFields}
      />

      <StateReasonModal
        open={reasonModalShown}
        onClose={closeReasonModal}
        onCancel={cancelReasonModal}
        modalType={reasonModalType}
        activeReason={shownStateReason}
        fields={editFields}
        fieldsHandler={handleEditFields}
      />
    </>
  );
};
